import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TextLink from 'LuxKit/TextLink'
import ModalContext from 'contexts/ModalContext'
import TasteOfGoldLearnMoreModal from './TasteOfGoldLearnMoreModal'
import TasteOfGoldHeroLogo from './TasteOfGoldHeroLogo'
import GoldText from './GoldText'
import { useLocation } from 'react-router-dom'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import { isBannerDismissed, recordSiteBannerDismissal } from 'storage/siteBanner'
import moment from 'moment'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import IconButton from 'LuxKit/Button/IconButton'
import { offerPageRoutes } from 'takeovers/utils/constants/takeoverRoutes'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getCanLoyaltyEnrollExistingUser from 'loyalty/selectors/getCanLoyaltyEnrollExistingUser'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import useLoyaltyUserEligibilityTier from 'loyalty/hooks/useLoyaltyUserEligibilityTier'
import { isLoyaltyMembershipActivationBannerDismissed } from 'actions/loyalty/membershipActivationBannerStorage'
import { FormattedMessage, useIntl } from 'react-intl'

const BANNER_ID = 'tasteOfGoldBanner'
const DISMISS_DAYS = 7

interface Props {
  dismissible?: boolean
  centered?: boolean
}

function TasteOfGoldBannerContent({ dismissible, centered }: Props) {
  const showModal = useContext(ModalContext)
  const location = useLocation()
  const [isDismissed, setIsDismissed] = useState(false)
  const canLoyaltyEnrollExistingUser = useAppSelector(getCanLoyaltyEnrollExistingUser)
  const [accountEligibleTier, accountEligibleTierLoading, accountEligibleTierError] = useLoyaltyUserEligibilityTier()
  const dispatch = useAppDispatch()

  const showActivateCta = canLoyaltyEnrollExistingUser &&
    !isLoyaltyMembershipActivationBannerDismissed() &&
    !!accountEligibleTier &&
    !accountEligibleTierLoading &&
    !accountEligibleTierError &&
    accountEligibleTier !== 'platinum'

  const activateMembership = useCallback(() => {
    dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
  }, [dispatch])

  useEffect(() => {
    if (dismissible) {
      setIsDismissed(isBannerDismissed(BANNER_ID))
    }
  }, [dismissible])

  const onDismiss = useCallback(() => {
    setIsDismissed(true)
    recordSiteBannerDismissal(BANNER_ID, moment().add(DISMISS_DAYS, 'days').toISOString())
  }, [])

  const isOfferPage = useMemo(
    () => offerPageRoutes.some(route => matchLocaleAwarePath(location.pathname, { path: route })),
    [location.pathname],
  )

  const openLearnMore = useCallback(() => {
    showModal(<TasteOfGoldLearnMoreModal />)
  }, [showModal])

  const intl = useIntl()

  if (dismissible && isDismissed) return null

  const program = getLoyaltyProgramDisplayName()
  const tier = getLoyaltyTierDisplayName('gold')

  const experienceLabel = intl.formatMessage(
    {
      id: 'O8_8PUrUCboT-LeG9d-B',
      defaultMessage: 'Experience {program} {tier}',
      description: 'Bold banner heading for Taste of Gold campaign in the site takeover promotion',
    },
    { program, tier },
  )
  const ctaText = showActivateCta
    ? intl.formatMessage({
        id: 'Hk9OWaNmlO56uONLZODf',
        defaultMessage: 'Activate for free',
        description: 'CTA to activate loyalty membership for free in Taste of Gold banner',
      })
    : intl.formatMessage({
        id: 'HPuoWmTsquSSVdf-yn_f',
        defaultMessage: 'Details',
        description: 'Button text to open the Taste of Gold offer details page from the promotional banner',
      })
  const ctaOnClick = showActivateCta ? activateMembership : openLearnMore

  return (
    <Group direction="horizontal" gap={12} verticalAlign="center" horizontalAlign={centered ? 'center' : undefined} fullWidth={centered}>
      <TasteOfGoldHeroLogo size="small" />
      <CSSBreakpoint max="tablet">
        <div>
          <GoldText><b>{experienceLabel}</b></GoldText>
          <div>
            {isOfferPage && <FormattedMessage
              id="0IP0Huqb0AA6pxsk7LiS"
              defaultMessage="Free airport transfer when you book this offer. Limited time only. <link>{ctaText}</link>"
              description="Offer page banner copy for Taste of Gold promotion with CTA"
              values={{
                ctaText,
                link: (chunks) => <TextLink wrap="no-wrap" onClick={ctaOnClick} colour="inherit">{chunks}</TextLink>,
              }}
            />}
            {!isOfferPage && <FormattedMessage
              id="_FxwTK3j7fZKV0q97GUB"
              defaultMessage="Free airport transfer for your next international escape. Limited time only. <link>{ctaText}</link>"
              description="General banner copy for Taste of Gold promotion with CTA"
              values={{
                ctaText,
                link: (chunks) => <TextLink wrap="no-wrap" onClick={ctaOnClick} colour="inherit">{chunks}</TextLink>,
              }}
            />}
          </div>
        </div>
      </CSSBreakpoint>
      <CSSBreakpoint min="desktop">
        <Group direction="horizontal" gap={12} verticalAlign="center" wrap="wrap">
          <GoldText><b>{experienceLabel}</b></GoldText>
          <span>
            {isOfferPage && <FormattedMessage
              id="0IP0Huqb0AA6pxsk7LiS"
              defaultMessage="Free airport transfer when you book this offer. Limited time only. <link>{ctaText}</link>"
              description="Offer page banner copy for Taste of Gold promotion with CTA"
              values={{
                ctaText,
                link: (chunks) => <TextLink wrap="no-wrap" onClick={ctaOnClick} colour="inherit">{chunks}</TextLink>,
              }}
            />}
            {!isOfferPage && <FormattedMessage
              id="_FxwTK3j7fZKV0q97GUB"
              defaultMessage="Free airport transfer for your next international escape. Limited time only. <link>{ctaText}</link>"
              description="General banner copy for Taste of Gold promotion with CTA"
              values={{
                ctaText,
                link: (chunks) => <TextLink wrap="no-wrap" onClick={ctaOnClick} colour="inherit">{chunks}</TextLink>,
              }}
            />}
          </span>
        </Group>
      </CSSBreakpoint>
      {dismissible && <IconButton kind="tertiary" variant="dark" size="small" onClick={onDismiss}><LineTimesIcon /></IconButton>}
    </Group>
  )
}

export default TasteOfGoldBannerContent
